/**
 * Android Chrome hand-off for the Garmin sign-in (#371).
 *
 * The Garmin Connect app claims every `connect.garmin.com` URL as a verified
 * App Link, but Chrome only leaves the tab for a JavaScript-initiated `https`
 * navigation in narrow cases, so the website stayed on the Garmin web page.
 * The documented way to ask Chrome for an app is the `intent://` URL form:
 * it opens the named package when installed and the gesture is fresh, and
 * otherwise loads `S.browser_fallback_url` - the plain authorize URL, i.e.
 * exactly the old behaviour. Pure functions so they are unit-testable.
 *
 * #376 added the second half of the lesson: WHICH URL matters less than HOW
 * it is followed. Every mobile browser honours the hand-off - iOS universal
 * link or Android intent - when the navigation is the user's own tap on a
 * real link; only Safari and Chrome are also lenient about a `location.href`
 * assignment made after an awaited API call. Edge on both platforms is not,
 * which is why the Connect button is now an <a href> prepared ahead of time.
 */
export const GARMIN_ANDROID_PACKAGE = "com.garmin.android.apps.connectmobile";

/**
 * Garmin Connect's iOS URL scheme (the Connect IQ hand-off one), used only to
 * ask whether the app is installed; it is listed under
 * LSApplicationQueriesSchemes in ios/App/App/Info.plist (#379). The sign-in
 * itself never opens this scheme: the authorize URL is a universal link the
 * Garmin app claims (`connect.garmin.com/oauth2Confirm`), so opening it
 * natively lands in the app when installed and in Safari otherwise.
 */
export const GARMIN_IOS_SCHEME = "gcm-ciq://";

/**
 * Chromium browsers on Android (Chrome, Samsung Internet, Edge) understand
 * `intent://`; Firefox does not. Note Edge only acts on it from a link tap.
 */
export function isAndroidChromium(userAgent: string): boolean {
  return /Android/.test(userAgent) && /Chrome\//.test(userAgent) && !/Firefox/.test(userAgent);
}

/** Wrap an https authorize URL in a Chrome intent that targets the Garmin Connect app. */
export function garminIntentUrl(authorizeUrl: string): string {
  const u = new URL(authorizeUrl);
  return (
    `intent://${u.host}${u.pathname}${u.search}` +
    `#Intent;scheme=https;package=${GARMIN_ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${encodeURIComponent(authorizeUrl)};end`
  );
}

/**
 * The href to put on the Connect button (#376): the intent form for Android
 * Chromium, the plain authorize URL for everyone else. In Safari on iOS the
 * plain URL is what Garmin's universal link turns into the app - provided it
 * is followed from a real link tap, which is the caller's job. (The native
 * shells do not use this: see lib/native/externalUrl.ts.)
 */
export function garminHref(authorizeUrl: string, userAgent: string): string {
  return isAndroidChromium(userAgent) ? garminIntentUrl(authorizeUrl) : authorizeUrl;
}

/**
 * How long a prepared href is trusted before it is fetched again. The
 * server-side OAuth state behind it lives OAUTH2_STATE_TTL_SECONDS (10 min,
 * pbranks-server/src/util/garminOAuth2.ts); refreshing well inside that
 * keeps a tap from ever carrying an expired state to Garmin.
 */
export const PREPARED_HREF_REFRESH_MS = 8 * 60 * 1000;

/** Whether a href prepared at `preparedAt` (epoch ms, or null for never) needs fetching again. */
export function isPreparedStale(
  preparedAt: number | null,
  now: number,
  ttlMs: number = PREPARED_HREF_REFRESH_MS,
): boolean {
  return preparedAt === null || now - preparedAt >= ttlMs;
}
